import {
  ConfigResponse,
  GenerateAudioApiRequest,
  GenerateAudioRequest,
  MessagesResponse,
  TranslateTextApiRequest,
  TranslateTextApiResponse,
  TranslateTextRequest,
  TranslateTextResponse,
  UserConfigResponse,
} from '@/types/client.d'
import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios'
import { validate } from '@/utils/validator.util'
import {
  ConfigResponseSchema,
  GenerateAudioRequestApiSchema,
  MessagesResponseSchema,
  TranslateTextApiRequestSchema,
  TranslateTextApiResponseSchema,
  UserConfigResponseSchema,
} from '@/validators/client.validator'
import { getRequestConfig, getAccessToken } from '@/api/authorisation'
import { instance } from '@/api/axiosConfig'
import { environment } from '@/environments/environment'

const client: AxiosInstance = axios.create({
  baseURL: `${environment.API_CLIENT}`,
})

client.interceptors.request.use((config) => {
  const accessToken: string = getAccessToken()
  config.headers.Authorization = `Bearer ${accessToken}`

  return config
})

export async function getConfig(): Promise<ConfigResponse> {
  const response: AxiosResponse = await client.post('/config')
  const configResponse: ConfigResponse = response.data
  validate(ConfigResponseSchema, configResponse)

  return configResponse
}

export async function getMessages(): Promise<MessagesResponse> {
  const response: AxiosResponse = await client.post('/messages')
  const messagesResponse: MessagesResponse = response.data
  validate(MessagesResponseSchema, messagesResponse)

  return messagesResponse
}

export async function getUserConfig(): Promise<UserConfigResponse> {
  const response: AxiosResponse = await client.post('/userConfig')
  const userConfigResponse: UserConfigResponse = response.data
  validate(UserConfigResponseSchema, userConfigResponse)

  return userConfigResponse
}

export async function translateText(
  translateTextRequest: TranslateTextRequest,
): Promise<TranslateTextResponse> {
  const config: AxiosRequestConfig = getRequestConfig()
  const translateTextApiRequest: TranslateTextApiRequest = {
    translateTextRequest: {
      ...translateTextRequest,
    },
  }
  validate(TranslateTextApiRequestSchema, translateTextApiRequest)

  const response: AxiosResponse = await client.post(
    '/translateText',
    translateTextApiRequest,
  )
  const translateTextApiResponse: TranslateTextApiResponse = response.data
  validate(TranslateTextApiResponseSchema, translateTextApiResponse)

  const translateTextResponse: TranslateTextResponse = translateTextApiResponse
  return translateTextResponse
}

export async function generateAudio(
  generateAudioRequest: GenerateAudioRequest,
): Promise<string> {
  const config: AxiosRequestConfig = getRequestConfig()
  config.responseType = 'arraybuffer'

  const generateAudioApiRequest: GenerateAudioApiRequest = {
    generateAudioRequest: {
      ...generateAudioRequest,
    },
  }
  validate(GenerateAudioRequestApiSchema, generateAudioApiRequest)

  const axiosResponse: AxiosResponse = await instance.post(
    '/generateAudio',
    generateAudioApiRequest,
    config,
  )
  const data: any = axiosResponse.data

  const blob: Blob = new Blob([data], {
    type: 'audio/wav',
  })

  const url: string = URL.createObjectURL(blob)
  return url
}
